/**
 * Input and output transformations for the DeFi module.
 *
 * @remarks
 * This file contains all mapping and transformation functions used by the DeFi module
 * to convert between internal SDK formats and external API formats.
 *
 * @internal
 */
import { normalizeChainID, normalizeChainIdString, normalizeChainIdsDeep, } from "../../shared/caip2";
import { ChainInvalidChainIdError } from "../../shared/error";
import { assertBridgeQuoteRequiredFields, assertSwapQuoteRequiredFields, hasDualDepositTxProperties, } from "./validate";
const isCaip2Format = (value) => /^[a-z0-9]{3,8}:[A-Za-z0-9._-]+$/.test(value);
function normalizeResponseChainId(value) {
    const raw = String(value ?? "").trim();
    if (!raw)
        return "";
    const normalized = normalizeChainIdString(raw);
    return isCaip2Format(normalized) ? normalized : "";
}
function normalizeChainIdsDeepToCaip2(input) {
    if (Array.isArray(input)) {
        return input.map((value) => normalizeChainIdsDeepToCaip2(value));
    }
    if (input !== null && typeof input === "object") {
        const out = {};
        for (const [key, value] of Object.entries(input)) {
            if (key === "chainId" || key === "chainID") {
                if (typeof value === "string") {
                    out[key] = normalizeChainIdString(value);
                }
                else if (typeof value === "number") {
                    out[key] = normalizeChainIdString(String(value));
                }
                else {
                    out[key] = value;
                }
            }
            else {
                out[key] = normalizeChainIdsDeepToCaip2(value);
            }
        }
        return out;
    }
    return input;
}
/**
 * Normalizes swap response data (any shape). Currently normalizes all chainId/chainID
 * fields to CAIP-2; preserves the full response structure and other fields unchanged.
 *
 * @internal
 */
export function normalizeSwapResponse(data) {
    return normalizeChainIdsDeepToCaip2(data);
}
/**
 * Transforms the raw DEX swap-with-approval response into the standardized SDK format.
 *
 * @internal
 */
export function transformSwapTransactionWithApprovalResponse(apiResponse, dexId, logger) {
    logger.debug("transformSwapTransactionWithApprovalResponse", { dexId });
    const normalized = normalizeSwapResponse((apiResponse.data ?? []));
    return {
        data: {
            swapWithApproveTxn: normalized,
        },
    };
}
/**
 * Normalizes bridge response data (any shape). Currently normalizes all chainId/chainID
 * fields to CAIP-2; preserves the full response structure and other fields unchanged.
 *
 * @internal
 */
export function normalizeBridgeResponse(data) {
    return normalizeChainIdsDeepToCaip2(data);
}
/**
 * Safely extracts the createFeeTxn from a lendborrow transaction object.
 *
 * @param obj - Object that might contain createFeeTxn
 * @returns The createFeeTxn as BridgeSwap200ResponseDataAnyOf1CreateFeeTxnPayload or empty object if not found
 */
function extractLendborrowCreateFeeTxn(obj) {
    if (hasDualDepositTxProperties(obj) && obj.createFeeTxn) {
        // If createFeeTxn exists, it should be a BridgeSwap200ResponseDataAnyOf1CreateFeeTxnPayload object
        const createFeeTxn = obj.createFeeTxn;
        if (createFeeTxn && typeof createFeeTxn === "object" && "chainId" in createFeeTxn) {
            const chainId = normalizeResponseChainId(createFeeTxn.chainId);
            return {
                ...createFeeTxn,
                chainId,
            };
        }
        return createFeeTxn;
    }
    // Return empty BridgeSwap200ResponseDataAnyOf1CreateFeeTxnPayload if not found
    return {
        chainId: "",
        from: "",
        to: "",
        value: "0",
        gas: "0",
        gasPrice: "",
    };
}
/**
 * Safely extracts an allowTxn property from a transaction object.
 *
 * @param obj - The transaction object that may contain an allowTxn property
 * @returns The allowTxn object or an empty object if not found
 */
function extractAllowTxn(obj) {
    if (hasDualDepositTxProperties(obj)) {
        const allowTxn = obj.allowTxn || {};
        if (allowTxn && typeof allowTxn === "object" && "chainId" in allowTxn) {
            const chainId = normalizeResponseChainId(allowTxn.chainId);
            return {
                ...allowTxn,
                chainId,
            };
        }
        return allowTxn;
    }
    return {};
}
/**
 * Transforms the raw Blockdaemon tokens API response into the standardized SDK price format.
 *
 * @remarks
 * This function processes the tokens API response to extract specific token pricing
 * information. It handles CAIP-2 chain ID format conversion and provides fallback
 * values for missing data.
 *
 * **Key Processing Steps:**
 * 1. Extracts token data from the chain-specific response structure
 * 2. Finds the specific token by address (case-insensitive)
 * 3. Converts CAIP-2 chain ID format back to numeric format
 * 4. Provides comprehensive fallback values for missing data
 *
 * **Chain ID Handling:**
 * - Input: "eip155:1" (CAIP-2 format)
 * - Output: "1" (numeric string format)
 *
 * @param apiResponse - Raw API response grouped by chain ID
 * @param address - Token contract address to search for
 * @param chainID - Target chain ID in numeric format
 * @returns Standardized price response
 *
 * @internal
 */
/**
 * Normalizes token address for API requests.
 * Maps the common native ETH placeholder address to zero address.
 *
 * @param address - Token address to normalize
 * @returns Normalized address
 * @internal
 */
function normalizeTokenAddress(address) {
    const NATIVE_ETH_PLACEHOLDER = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";
    const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
    return address.toLowerCase() === NATIVE_ETH_PLACEHOLDER.toLowerCase() ? ZERO_ADDRESS : address;
}
/**
 * Builds the portfolio tokens API request URL with query parameters.
 *
 * @param basePath - Base URL for the API (e.g., "https://svc.blockdaemon.com")
 * @param request - Request parameters (tokenAddress, tokenSymbol, chainID)
 * @returns Complete URL with query parameters
 * @internal
 */
export function buildPortfolioTokensRequestUrl(basePath, request) {
    const url = new URL(`${basePath}/portfolio/v1/tokens`);
    // Add query parameters
    if (request.tokenAddress) {
        const normalizedAddress = normalizeTokenAddress(String(request.tokenAddress));
        url.searchParams.append("tokenAddress", normalizedAddress);
    }
    if (request.tokenSymbol) {
        url.searchParams.append("tokenSymbol", String(request.tokenSymbol));
    }
    if (request.chainID) {
        url.searchParams.append("chainID", String(request.chainID));
    }
    if (request.tagLimit && Array.isArray(request.tagLimit)) {
        // tagLimit can be an array, add each tag as a separate query parameter
        for (const tag of request.tagLimit) {
            url.searchParams.append("tagLimit", String(tag));
        }
    }
    // Use provided fiatType or default to USD
    const fiatType = request.fiatType || "USD";
    url.searchParams.append("fiatType", fiatType);
    return url;
}
/**
 * Transform raw API response from tokens endpoint to standardized format.
 *
 * @remarks
 * This function transforms the raw API response from the /portfolio/v1/tokens endpoint
 * into a standardized format that matches our SDK's expected interface.
 * It handles various token metadata fields and ensures consistent data types.
 *
 * **Key Transformations:**
 * - Converts CAIP-2 format chain IDs (eip155:1) to numeric format (1)
 * - Ensures all required fields have default values
 * - Converts price data to string format for consistency
 * - Provides empty arrays for missing tags
 * - Supplies empty objects for missing extensions
 * - Filters tokens to only include those matching the requested chainId
 *
 * @param apiResponse - Raw API response with chain-grouped token data
 * @param requestedChainId - The original chainId that was requested (optional for backwards compatibility)
 * @returns Standardized tokens response matching IGetAssetsResponse interface
 *
 * @internal
 */
export function transformTokensApiResponse(apiResponse) {
    // Handle the actual API response format from /portfolio/v1/tokens
    // The response is an object with chainID as key and array of tokens as value
    const allTokens = [];
    // Extract tokens from all chains
    for (const chainKey of Object.keys(apiResponse)) {
        const tokens = apiResponse[chainKey] || [];
        allTokens.push(...tokens);
    }
    return {
        data: allTokens.map((token) => {
            const fallbackChainId = (token.chainID || "").trim();
            const chainId = normalizeResponseChainId(fallbackChainId);
            if (!chainId) {
                throw new ChainInvalidChainIdError(token.chainID || "", "unknown", `Could not normalize to CAIP-2 chain ID for token "${token.symbol || token.name || token.address}"`);
            }
            // Type assertion needed as the type definition may not include prices
            const tokenWithPrices = token;
            return {
                chainId,
                address: token.address || "",
                name: token.name || "",
                symbol: token.symbol || "",
                decimals: token.decimals || 0,
                logoURI: token.logoURI,
                tags: token.tags || [],
                // Only include priceUSD if available
                ...(token.priceUSD != null && { priceUSD: token.priceUSD.toString() }),
                // Only include prices if available
                ...(tokenWithPrices.prices && { prices: tokenWithPrices.prices }),
                extensions: token.extensions,
            };
        }),
    };
}
/**
 * Transforms the Blockdaemon DEX quote aggregator response into the standardized SDK format.
 *
 * @remarks
 * This function handles the transformation of swap quote data from the
 * Blockdaemon API DexApi.dexQuoteaggregator response to the standardized SDK response format.
 * It ensures consistent data structure and handles missing or undefined values
 * by providing sensible defaults.
 *
 * **Transformation Process:**
 * 1. Extracts quote data from the API response
 * 2. Processes DEX quotes array and standardizes field names
 * 3. Applies default values for missing fields
 * 4. Ensures type consistency across all fields
 *
 * **Data Normalization:**
 * - Missing quotes default to empty array
 * - Missing DEX metadata defaults to empty strings
 * - Missing amounts default to "0"
 * - Missing paths default to empty array
 *
 * @param apiResponse - API response from DexApi.dexQuoteaggregator
 * @param chainId - Original chain ID for response consistency
 * @returns Standardized swap quote response matching IBuildSwapQuoteResponse interface
 *
 * @internal
 */
export function transformSwapQuoteResponse(apiResponse, chainId, logger) {
    logger.debug("transformSwapQuoteResponse", {
        chainId,
        quotesCount: apiResponse.data?.length ?? 0,
    });
    const normalizedChainId = normalizeChainID(chainId, "eip155");
    // Handle the API response format
    const items = apiResponse.data || [];
    const quotes = items.map((q) => {
        const { path, amountIn, amountsOut, providerId, providerName } = assertSwapQuoteRequiredFields({
            chainId: normalizedChainId,
            path: q.path,
            amountIn: q.amountIn,
            amountsOut: q.amountsOut,
            dexId: q.dexId,
            dexName: q.dexName,
            bridgeId: q.bridgeId,
            bridgeName: q.bridgeName,
        });
        return {
            chainId: normalizedChainId,
            path,
            amountIn,
            amountsOut,
            providerId,
            providerName,
        };
    });
    return { data: quotes };
}
/**
 * Transforms the Blockdaemon bridge quote aggregator response into the standardized SDK format.
 *
 * @remarks
 * This function handles the transformation of bridge quote data from the
 * Blockdaemon API BridgeApi.bridgeQuoteaggregator response to the standardized SDK response format.
 * It ensures consistent data structure and handles missing or undefined values
 * by providing sensible defaults.
 *
 * **Transformation Process:**
 * 1. Extracts quote data from the API response
 * 2. Processes bridge quotes array and standardizes field names
 * 3. Applies default values for missing fields
 * 4. Ensures type consistency across all fields
 *
 * **Data Normalization:**
 * - Missing quotes default to empty array
 * - Missing bridge metadata defaults to empty strings
 * - Missing amounts default to "0"
 * - Missing chain IDs default to empty strings
 *
 * @param apiResponse - API response from BridgeApi.bridgeQuoteaggregator
 * @param srcChainId - Source chain ID for response consistency
 * @param dstChainId - Destination chain ID for response consistency
 * @returns Standardized bridge quote response matching IBuildBridgeQuoteResponse interface
 *
 * @internal
 */
export function transformBridgeQuoteResponse(apiResponse, srcChainId, dstChainId, logger) {
    logger.debug("transformBridgeQuoteResponse", {
        srcChainId,
        dstChainId,
        quotesCount: apiResponse.data?.length ?? 0,
    });
    const normalizedSrcChainId = srcChainId ? normalizeResponseChainId(srcChainId) : undefined;
    const normalizedDstChainId = normalizeResponseChainId(dstChainId);
    if (!normalizedDstChainId) {
        throw new ChainInvalidChainIdError(`src chain ${srcChainId}`, `dst chain ${dstChainId}`, "Could not normalize bridge quote chain IDs to CAIP-2");
    }
    if (!normalizedSrcChainId) {
        logger.warn("empty source chain ID (optional)");
    }
    // Handle the API response format
    const items = apiResponse.data || [];
    const quotes = items.map((b) => {
        const { srcTokenSymbol, dstTokenSymbol, amountIn, amountsOut, providerId, providerName } = assertBridgeQuoteRequiredFields({
            srcChainId: normalizedSrcChainId ?? "",
            dstChainId: normalizedDstChainId,
            srcTokenSymbol: b.srcTokenSymbol,
            dstTokenSymbol: b.dstTokenSymbol,
            amountIn: b.amountIn,
            amountsOut: b.amountsOut,
            bridgeId: b.bridgeId,
            bridgeName: b.bridgeName,
            dexId: b.dexId,
            dexName: b.dexName,
        });
        return {
            srcChainId: normalizedSrcChainId || undefined,
            dstChainId: normalizedDstChainId,
            srcTokenSymbol,
            dstTokenSymbol,
            amountIn,
            amountsOut,
            providerId,
            providerName,
        };
    });
    return { data: quotes };
}
/**
 * Transforms bridge status response from the API to our standardized format.
 *
 * @remarks
 * This function handles the transformation of bridge status data from the
 * Blockdaemon API BridgeApi.bridgeTransactions response to the standardized SDK response format.
 * It ensures consistent data structure and handles missing or undefined values
 * by providing sensible defaults.
 *
 * **Transformation Process:**
 * 1. Extracts status data from the API response
 * 2. Processes status data and standardizes field names
 * 3. Applies default values for missing fields
 * 4. Ensures type consistency across all fields
 *
 * **Data Normalization:**
 * - Missing status fields default to empty strings
 * - Missing chain IDs default to empty strings
 * - Missing addresses default to empty strings
 * - Missing amount defaults to "0"
 * - Missing timestamp defaults to empty string
 *
 * @param apiResponse - API response from BridgeApi.bridgeTransactions
 * @param bridgeId - Bridge identifier for response consistency
 * @returns Standardized bridge status response matching IGetBridgeStatusResponse interface
 * @note Uses type assertions because the DeFi SDK response types are incomplete for bridge status data
 *
 * @internal
 */
export function transformBridgeStatusResponse(apiResponse, bridgeId, logger) {
    logger.debug("transformBridgeStatusResponse", {
        bridgeId,
        status: apiResponse.data?.status,
    });
    const responseData = apiResponse.data || {};
    const srcTx = (responseData.srcTx || {});
    const dstTx = (responseData.dstTx || {});
    // If srcTx or dstTx are error objects (have message/code), return only the error info
    const safeSrcTx = srcTx.message || srcTx.code
        ? { message: srcTx.message, code: srcTx.code }
        : {
            ...srcTx,
            chainId: normalizeResponseChainId(srcTx.chainId),
            hash: srcTx.hash || "",
            transactionStatus: srcTx.transactionStatus || false,
            blockNumber: srcTx.blockNumber || "",
            timestamp: srcTx.timestamp || "",
            from: srcTx.from || "",
            to: srcTx.to || "",
            value: srcTx.value || "0",
            transactionFees: srcTx.transactionFees || "0",
            gas: srcTx.gas || "0",
            gasPrice: srcTx.gasPrice || "0",
            input: srcTx.input || "",
            nonce: srcTx.nonce || 0,
            type: srcTx.type || 0,
            network: srcTx.network || null,
            v: srcTx.v || "",
            r: srcTx.r || "",
            s: srcTx.s || "",
            yParity: srcTx.yParity || "",
            transactionIndex: srcTx.transactionIndex || 0,
            accessList: srcTx.accessList || [],
            maxFeePerGas: srcTx.maxFeePerGas || "",
            maxPriorityFeePerGas: srcTx.maxPriorityFeePerGas || "",
            logs: srcTx.logs || [],
        };
    const safeDstTx = dstTx.message || dstTx.code
        ? { message: dstTx.message, code: dstTx.code }
        : {
            ...dstTx,
            chainId: normalizeResponseChainId(dstTx.chainId),
            hash: dstTx.hash || "",
            transactionStatus: dstTx.transactionStatus || false,
            blockNumber: dstTx.blockNumber || "",
            timestamp: dstTx.timestamp || "",
            from: dstTx.from || "",
            to: dstTx.to || "",
            value: dstTx.value || "0",
            transactionFees: dstTx.transactionFees || "0",
            gas: dstTx.gas || "0",
            gasPrice: dstTx.gasPrice || "0",
            input: dstTx.input || "",
            nonce: dstTx.nonce || 0,
            type: dstTx.type || 0,
            network: dstTx.network || null,
            v: dstTx.v || "",
            r: dstTx.r || "",
            s: dstTx.s || "",
            yParity: dstTx.yParity || "",
            transactionIndex: dstTx.transactionIndex || 0,
            accessList: dstTx.accessList || [],
            maxFeePerGas: dstTx.maxFeePerGas || "",
            maxPriorityFeePerGas: dstTx.maxPriorityFeePerGas || "",
            logs: dstTx.logs || [],
        };
    return {
        data: {
            status: responseData.status || "",
            srcTx: safeSrcTx,
            dstTx: safeDstTx,
        },
    };
}
/**
 * Transforms lendborrow deposit API response to standardized format.
 *
 * @remarks
 * This function handles the transformation of lendborrow deposit data from the
 * Blockdaemon API response to the standardized SDK response format.
 * It ensures consistent data structure and handles missing or undefined values
 * by providing sensible defaults.
 *
 * **Transformation Process:**
 * 1. Extracts deposit data from the API response
 * 2. Creates standardized response object with proper field mapping
 * 3. Applies default values for missing fields using optional chaining
 * 4. Ensures type consistency across all fields
 *
 * **Data Normalization:**
 * - Missing status defaults to 0
 * - Missing message defaults to empty string
 * - Missing data defaults to undefined
 * - Uses optional chaining for safe property access
 *
 * @param response - The raw API response from Blockdaemon
 * @returns Standardized lendborrow deposit response with only data field
 *
 * @internal
 */
export function transformLendborrowDepositResponse(response, logger) {
    logger.debug("transformLendborrowDepositResponse");
    const responseData = response.data;
    // Helper to normalize a depositTxn object into LendborrowAllow200ResponseDataAnyOfPayload
    const mapDepositTxn = (tx) => {
        if (!tx || typeof tx !== "object") {
            throw new Error("Invalid depositTxn data: expected object");
        }
        const obj = tx;
        // Support various data field names from upstream (data | txData | calldata | input)
        const rawData = obj.data ??
            obj.txData ??
            obj.calldata ??
            obj.input;
        const dataJoined = Array.isArray(rawData)
            ? rawData.join("")
            : rawData;
        const chainId = normalizeResponseChainId(obj.chainId);
        const from = obj.from;
        const to = obj.to;
        const gas = obj.gas || obj.gasLimit;
        const value = obj.value;
        const dataHex = (dataJoined ?? "");
        return {
            chainId,
            from: from || "",
            to: to || "",
            gas: gas || "0",
            value: value || "0",
            gasPrice: obj.gasPrice || "",
            data: dataHex,
            estimationCheck: obj.estimationCheck || false,
            referenceId: obj.referenceId || "",
        };
    };
    // Handle dual transaction response (enableFee: true)
    if (responseData.createFeeTxn) {
        return {
            data: {
                createFeeTxn: extractLendborrowCreateFeeTxn({
                    createFeeTxn: responseData.createFeeTxn,
                }),
                depositTxn: mapDepositTxn(responseData.depositTxn),
            },
        };
    }
    // Handle single transaction response (enableFee: false)
    return { data: mapDepositTxn(responseData) };
}
/**
 * Transforms lendborrow withdraw response from the API to our standardized format.
 *
 * @remarks
 * This function handles the transformation of lendborrow withdraw data from the
 * Blockdaemon API LendborrowApi.lendborrowWithdraw response to the standardized SDK response format.
 * It ensures consistent data structure and handles missing or undefined values
 * by providing sensible defaults.
 *
 * **Transformation Process:**
 * 1. Extracts withdraw data from the API response
 * 2. Processes withdraw data and standardizes field names
 * 3. Applies default values for missing fields
 * 4. Ensures type consistency across all fields
 *
 * **Data Normalization:**
 * - Missing withdraw fields default to empty strings
 * - Missing transaction data defaults to empty strings
 * - Missing gas and value default to "0"
 *
 * @param apiResponse - API response from LendborrowApi.lendborrowWithdraw
 * @returns Standardized lendborrow withdraw response matching ILendBorrowWithdrawResponse interface
 *
 * @internal
 */
export function transformLendBorrowWithdrawResponse(apiResponse, logger) {
    logger.debug("transformLendBorrowWithdrawResponse");
    const responseData = apiResponse.data;
    const withdrawData = {
        chainId: normalizeResponseChainId(responseData?.chainId),
        from: responseData?.from || "",
        to: responseData?.to || "",
        value: responseData?.value || "0",
        gas: responseData?.gas || "0",
        data: responseData?.data || "",
        estimationCheck: responseData?.estimationCheck ||
            false,
        referenceId: responseData?.referenceId || "",
        createFeeTxn: extractLendborrowCreateFeeTxn(responseData),
        allowTxn: extractAllowTxn(responseData),
    };
    return { data: withdrawData };
}
// Generic deep normalization for chainId fields
// moved to shared/caip2.ts and imported above
export function transformUserPositionsResponse(apiResponse, logger) {
    logger.debug("transformUserPositionsResponse");
    const normalized = normalizeChainIdsDeep(apiResponse.data ?? []);
    return { data: normalized };
}
export function transformLendborrowPoolResponse(apiResponse, logger) {
    logger.debug("transformLendborrowPoolResponse");
    const normalized = normalizeChainIdsDeep(apiResponse.data ?? []);
    return { data: normalized };
}
export function transformLendborrowPoolsResponse(apiResponse, logger) {
    logger.debug("transformLendborrowPoolsResponse");
    const normalized = normalizeChainIdsDeep(apiResponse.data ?? {});
    return { data: normalized };
}
export function transformApiMetadataV2Response(apiResponse, logger) {
    logger.debug("transformApiMetadataV2Response");
    const data = apiResponse.data;
    if (!data)
        return apiResponse;
    const normalized = normalizeChainIdsDeepToCaip2(data);
    return {
        ...apiResponse,
        data: normalized,
    };
}
//# sourceMappingURL=data:application/json;base64,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